import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Modal, Typography, Upload, Button, Dropdown, message } from 'antd'
import { Icon } from '@iconify/react'
import { importExcelTasksThunk } from '../../taskRefactored/tasks.slice'
import { downloadTaskImportSample } from '../../taskRefactored/tasks.service'
import { showActionNotification } from '../../../components/notification'

const { Title, Text } = Typography

const ImportTasksModal = ({ open, onCancel, projectId, onImportSuccess }) => {
    const dispatch = useDispatch()
    const [fileList, setFileList] = useState([])
    const [loading, setLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)

    // Clear file list when modal is closed
    useEffect(() => {
        if (!open) {
            setFileList([])
            setDownloading(false)
        }
    }, [open])

    const handleDownloadSample = async (format) => {
        setDownloading(true)
        try {
            const result = await downloadTaskImportSample(format)
            if (!result?.success) {
                message.error(result?.message || 'Failed to download sample file')
            }
        } finally {
            setDownloading(false)
        }
    }

    const handleContinue = async () => {
        if (!fileList || fileList.length === 0) {
            message.warning('Please select a file to import')
            return
        }

        if (!projectId) {
            message.error('Project ID is required')
            return
        }

        setLoading(true)
        try {
            const file = fileList[0].originFileObj || fileList[0]
            const result = await dispatch(
                importExcelTasksThunk({
                    file,
                    projectId,
                })
            )

            const data = result?.payload?.data

            if (data) {
                const { success, message: responseMessage } = data

                if (success) {
                    showActionNotification({
                        message: 'Tasks imported successfully!',
                        subMessage: responseMessage?.message,
                    })
                    setFileList([])
                    if (onImportSuccess) {
                        onImportSuccess()
                    }
                    onCancel()
                } else {
                    setFileList([])
                    showActionNotification({
                        message: 'Failed to import tasks',
                        subMessage: responseMessage?.error,
                    })
                }
            }
        } catch (error) {
            showActionNotification({
                message: 'Failed to import tasks',
            })
        } finally {
            setFileList([])
            setLoading(false)
        }
    }

    const handleFileChange = (info) => {
        const { fileList: newFileList } = info
        setFileList(newFileList)
    }

    const handleRemove = () => {
        setFileList([])
    }

    const sampleMenuItems = [
        {
            key: 'xlsx',
            label: 'Excel (.xlsx)',
            icon: <Icon icon="mdi:file-excel-outline" width={16} height={16} />,
            onClick: () => handleDownloadSample('xlsx'),
        },
        {
            key: 'csv',
            label: 'CSV (.csv)',
            icon: <Icon icon="mdi:file-delimited-outline" width={16} height={16} />,
            onClick: () => handleDownloadSample('csv'),
        },
    ]

    return (
        <Modal
            title={null}
            open={open}
            onCancel={onCancel}
            footer={null}
            width={600}
            centered
            closeIcon={
                <Icon
                    icon="mdi:close"
                    style={{ fontSize: 20, color: '#64748b' }}
                />
            }
            styles={{
                content: {
                    borderRadius: '12px',
                    padding: '0',
                },
                body: {
                    padding: '32px',
                },
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                }}
            >
                {/* Modal Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Icon
                        icon="mdi:sparkles"
                        width={20}
                        height={20}
                        style={{ color: '#6366f1' }}
                    />
                    <Title
                        level={4}
                        style={{
                            margin: 0,
                            fontSize: '20px',
                            fontWeight: 600,
                        }}
                    >
                        Import tasks
                    </Title>
                </div>

                {/* Instructions */}
                <Text
                    style={{
                        fontSize: '14px',
                        color: '#64748b',
                        lineHeight: '20px',
                    }}
                >
                    Upload an Excel (.xlsx, .xls) or CSV file to import multiple
                    tasks into this project at once.
                </Text>

                {/* Sample download */}
                <div>
                    <Dropdown
                        menu={{ items: sampleMenuItems }}
                        trigger={['click']}
                        disabled={downloading}
                    >
                        <Button
                            loading={downloading}
                            style={{
                                height: '36px',
                                padding: '0 14px',
                                borderRadius: '10px',
                                border: '1px solid #e2e8f0',
                                background: '#ffffff',
                                color: '#475569',
                                fontSize: '13px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    lineHeight: 1,
                                }}
                            >
                                {!downloading && (
                                    <Icon
                                        icon="mdi:download"
                                        width={16}
                                        height={16}
                                        style={{ display: 'block' }}
                                    />
                                )}
                                <span style={{ lineHeight: '16px' }}>
                                    {downloading
                                        ? 'Downloading...'
                                        : 'Download sample'}
                                </span>
                                {!downloading && (
                                    <Icon
                                        icon="mdi:chevron-down"
                                        width={16}
                                        height={16}
                                        style={{ display: 'block' }}
                                    />
                                )}
                            </span>
                        </Button>
                    </Dropdown>
                </div>

                {/* File Upload Area */}
                <Upload.Dragger
                    style={{
                        border: '2px dashed #cbd5e1',
                        borderRadius: '8px',
                        backgroundColor: '#f8fafc',
                        padding: '40px 20px',
                    }}
                    fileList={fileList}
                    onChange={handleFileChange}
                    onRemove={handleRemove}
                    beforeUpload={() => false}
                    accept=".xlsx,.xls,.csv"
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <div
                            style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '8px',
                                backgroundColor: '#e0e7ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Icon
                                icon="mdi:file-upload-outline"
                                width={24}
                                height={24}
                                style={{ color: '#6366f1' }}
                            />
                        </div>
                        <Text
                            style={{
                                fontSize: '14px',
                                fontWeight: 500,
                                color: 'var(--textColorDarkSlate)',
                            }}
                        >
                            Select or drop a file
                        </Text>
                    </div>
                </Upload.Dragger>

                {/* Continue Button */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '8px',
                    }}
                >
                    <Button
                        onClick={handleContinue}
                        loading={loading}
                        disabled={!fileList || fileList.length === 0}
                        style={{
                            height: '40px',
                            padding: '0 16px',
                            borderRadius: '8px',
                            fontWeight: 500,
                            fontSize: '14px',
                            border: '1px solid #e2e8f0',
                            background: '#ffffff',
                            color: '#475569',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

export default ImportTasksModal
